"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  getTimezone,
  setTimezone as saveTimezone,
  fetchOrGetTimezones,
  getImmediateTimezones,
  TIMEZONE_CHANGED_EVENT,
  type TimezoneOption,
} from "@/lib/timezone";

type TimezoneContextType = {
  timezone: string;
  setTimezone: (tz: string) => void;
  options: TimezoneOption[];
  isLoadingOptions: boolean;
};

function buildDefaultOptions(): TimezoneOption[] {
  try {
    return getImmediateTimezones();
  } catch {
    return [
      {
        value: "UTC",
        label: "(UTC+00:00) UTC",
        offset: "UTC+00:00",
      },
    ];
  }
}

const DEFAULT_OPTIONS = buildDefaultOptions();

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: "UTC",
  setTimezone: () => {},
  options: DEFAULT_OPTIONS,
  isLoadingOptions: true,
});

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const [timezone, setTimezoneState] = useState<string>(() => getTimezone());
  const [options, setOptions] = useState<TimezoneOption[]>(() => buildDefaultOptions());
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(true);

  useEffect(() => {
    try {
      setTimezoneState(getTimezone());
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handleTimezoneChange = (e: Event) => {
      try {
        const customEvent = e as CustomEvent<{ timezone: string }>;
        if (customEvent.detail?.timezone) {
          setTimezoneState(customEvent.detail.timezone);
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChange);
    return () => {
      window.removeEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadOptions() {
      console.log("[TimezoneProvider] Starting to load full timezone list");
      setIsLoadingOptions(true);
      try {
        const list = await fetchOrGetTimezones();
        console.log("[TimezoneProvider] Received timezone list:", list.length);
        if (isMounted) {
          if (Array.isArray(list) && list.length > 0) {
            setOptions(list);
            console.log("[TimezoneProvider] Updated options state with", list.length, "timezones");
            setIsLoadingOptions(false);
          } else {
            console.error("[TimezoneProvider] Timezone list empty after fetch, keeping fallback");
            setIsLoadingOptions((prev) => {
              try {
                const currentOpts = buildDefaultOptions();
                if (currentOpts.length === 0) {
                  return prev;
                }
                return false;
              } catch {
                return prev;
              }
            });
          }
        }
      } catch (err) {
        console.error("[TimezoneProvider] Failed to load timezone options:", err);
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    }
    void loadOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoadingOptions && options.length === 0) {
      console.warn("[TimezoneProvider] options empty with loading done — injecting fallback");
      setOptions(buildDefaultOptions());
    }
  }, [options, isLoadingOptions]);

  const updateTimezone = useCallback((newTz: string) => {
    try {
      saveTimezone(newTz);
      setTimezoneState(newTz);
    } catch (err) {
      console.error("[TimezoneProvider] Failed to update timezone:", err);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      timezone,
      setTimezone: updateTimezone,
      options: options.length > 0 ? options : DEFAULT_OPTIONS,
      isLoadingOptions: isLoadingOptions && options.length === 0,
    }),
    [timezone, updateTimezone, options, isLoadingOptions],
  );

  return (
    <TimezoneContext.Provider value={contextValue}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  return useContext(TimezoneContext);
}
